import { NgClass } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';

@Component({
  selector: 'app-otp-input',
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.scss'],
  imports: [NgClass],
  standalone: true,
})
export class OtpInputComponent implements OnInit, AfterViewInit {

  @Input() length = 6; // dynamic: 4, 6, etc.
  @Input() isPassword = false; // optional: show characters as •
  @Output() otpChange = new EventEmitter<string>();
  @Output() otpEnterPress = new EventEmitter<void>();

  protected otpValues: string[] = [];

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

  ngOnInit() {
    this.otpValues = Array(this.length).fill('');
  }

  ngAfterViewInit(): void {
    this.otpInputs.first.nativeElement.focus();
  }

  protected onInput(event: any, index: number) {
    const originalValue = event.target.value;
    const cleanValue = originalValue.replace(/[^0-9A-Za-z]/g, '');

    // Improved autofill detection logic
    // Only treat as autofill when:
    // 1. We're in the first input AND we receive multiple characters (typical autofill behavior)
    // 2. OR we receive a very long string (6+ characters for password managers)
    const isAutofill = (index === 0 && cleanValue.length > 1) || cleanValue.length >= this.length;

    if (isAutofill) {
      // Handle autofill case - distribute across all inputs
      const chars = cleanValue.slice(0, this.length).split('');

      // Clear all inputs first
      this.otpValues.fill('');

      // Fill with autofilled characters
      chars.forEach((char, idx) => {
        if (idx < this.length) {
          this.otpValues[idx] = char;
          // Update the input field value as well
          this.otpInputs.toArray()[idx].nativeElement.value = char;
        }
      });

      // Focus on the next empty input or the last input
      setTimeout(() => {
        const nextIndex = chars.length < this.length ? chars.length : this.length - 1;
        this.otpInputs.toArray()[nextIndex].nativeElement.focus();
      });

      this.emitOtp();
      return;
    }

    // Normal single character input
    const value = cleanValue.charAt(0) || '';

    // Clear the input field to prevent invalid characters from showing
    event.target.value = value;

    // const previousValue = this.otpValues[index];
    this.otpValues[index] = value;

    // Move to next input if:
    // 1. We have a valid value AND
    // 2. We're not on the last input
    if (value && index < this.length - 1) {
      this.otpInputs.toArray()[index + 1].nativeElement.focus();
    }

    this.emitOtp();

    // If all inputs cleared, move focus to first box
    if (this.otpValues.every(val => val === '')) {
      setTimeout(() => {
        this.otpInputs.first.nativeElement.focus();
      }, 0);
    }
  }

  protected onFocus(event: any, index: number) {
    // Select existing character to make it easy to replace
    if (this.otpValues[index]) {
      event.target.select();
    }
  }

  protected onKeyDown(event: KeyboardEvent, idx: number) {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        if (idx < this.length - 1) this.otpInputs.toArray()[idx + 1].nativeElement.focus();
        break;

      case 'ArrowLeft':
        event.preventDefault();
        if (idx > 0) this.otpInputs.toArray()[idx - 1].nativeElement.focus();
        break;

      case 'Delete':
        event.preventDefault();
        this.otpValues[idx] = '';
        this.emitOtp();
        break;

      case 'Backspace':
        // Clear current or move & clear previous
        if (this.otpValues[idx]) {
          this.otpValues[idx] = '';
          this.emitOtp();
        } else if (idx > 0) {
          this.otpInputs.toArray()[idx - 1].nativeElement.focus();
          this.otpValues[idx - 1] = '';
          this.emitOtp();
        }
        break;

      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault(); // Optional: block up/down arrows
        break;

      case ' ':
        event.preventDefault(); // Block space key
        break;

      case 'Enter':
        event.preventDefault();
        this.otpEnterPress.emit();
        break;

      default:
        // Block any other invalid characters
        if (!/[0-9A-Za-z]/.test(event.key) &&
          !['Tab', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Escape'].includes(event.key)) {
          event.preventDefault();
        }
        break;
    }
  }


  private emitOtp() {
    this.otpChange.emit(this.otpValues.join(''));
  }

  protected onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasteData = event.clipboardData?.getData('text') || '';
    const chars = pasteData.replace(/[^0-9A-Za-z]/g, '').slice(0, this.length).split('');

    // Clear all inputs first
    this.otpValues.fill('');

    // Fill with pasted characters
    chars.forEach((char, idx) => {
      if (idx < this.length) {
        this.otpValues[idx] = char;
        // Update the input field value as well
        this.otpInputs.toArray()[idx].nativeElement.value = char;
      }
    });

    setTimeout(() => {
      const nextIndex = chars.length < this.length ? chars.length : this.length - 1;
      this.otpInputs.toArray()[nextIndex].nativeElement.focus();
    });
    this.emitOtp();
  }
}
